import { cn } from '../../lib/utils';

interface TypographyProps {
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * Renderer function registered by `plugin-docs` (or any other docs provider)
 * via `registerDocsHintRenderer()`. When `<PanelTitle docs="some/slug">` is
 * rendered, the registered function is called with the slug and its return
 * value is shown next to the title text. When no renderer is registered, the
 * slot renders nothing — `PanelTitle` stays decoupled from the docs feature.
 */
type DocsHintRenderer = (props: { slug: string }) => React.ReactNode;
let docsHintRenderer: DocsHintRenderer | null = null;

/**
 * Register the renderer that backs `<PanelTitle docs="slug">`. Called once at
 * module load from `plugin-docs/web/register.ts`. Passing `null` clears the
 * renderer (useful in tests).
 */
export function registerDocsHintRenderer(renderer: DocsHintRenderer | null): void {
  docsHintRenderer = renderer;
}

interface PageTitleProps extends TypographyProps {
  /** Optional leading icon rendered before the title text. */
  icon?: React.ReactNode;
  /**
   * Optional docs reference rendered next to the title text. Accepts the
   * same forms as `PanelTitle.docs` — a slug string forwarded to the
   * renderer registered by `plugin-docs` via `registerDocsHintRenderer()`,
   * or a custom `ReactElement` for ad-hoc info affordances.
   */
  docs?: string | React.ReactElement;
}

interface PanelTitleProps extends TypographyProps {
  /**
   * HTML element to render. Defaults to 'h1'. Use 'h2'/'h3' when PanelTitle is nested inside a page that already has an h1.
   * Use 'span' or 'p' inside buttons or other interactive triggers where a heading would be invalid HTML.
   */
  as?: 'h1' | 'h2' | 'h3' | 'span' | 'p';
  /** Size variant. 'sm' renders at text-sm for dense UI contexts (stream views, card headers). */
  size?: 'sm' | 'default';
  /**
   * When true (default), truncates to a single ellipsized line. Set false to
   * wrap. Applies to the text node (including the inner span when `docs` is set).
   */
  truncate?: boolean;
  /**
   * Optional docs reference rendered next to the title text. Two forms:
   * - **Slug string** (preferred): `<PanelTitle docs="concepts/agents">` —
   *   defers to the renderer registered by `plugin-docs` via
   *   `registerDocsHintRenderer()`. Renders nothing if no renderer is registered.
   *   May include a trailing `#anchor` to deep-link to a specific heading or
   *   block within the doc (e.g. `plugins/enabling-plugins#prerequisites`).
   * - **Custom node**: pass an arbitrary `ReactElement` to opt out of the
   *   docs-hint flow (e.g. inline tooltip, custom info button).
   */
  docs?: string | React.ReactElement;
}

interface SectionLabelProps extends TypographyProps {
  /** HTML element to render. Defaults to 'h2'. Use 'h3'/'h4' inside panels, 'span' inside flex sidebars, 'dt' inside description lists, 'p' for a subtitle under a heading. */
  as?: 'h2' | 'h3' | 'h4' | 'span' | 'dt' | 'p';
  /**
   * Visual emphasis of the label tone. Defaults to `'muted'`.
   * - `'muted'` — the faint micro-label used for sidebar / list-group
   *   headers (`text-muted-foreground/60`).
   * - `'strong'` — a more legible tone for content-section headings inside
   *   detail panels, cards, and collapsible sections (`text-foreground/70`).
   */
  tone?: 'muted' | 'strong';
}

/**
 * Top-level page heading (h1).
 * Used by PageHeader and any standalone page that needs a primary title.
 * Responsive: text-xl on mobile, text-2xl on sm+.
 */
export function PageTitle({ children, className, icon, docs }: PageTitleProps) {
  const docsNode =
    typeof docs === 'string' ? (docsHintRenderer ? docsHintRenderer({ slug: docs }) : null) : docs;

  return (
    <h1
      data-testid="page-title"
      className={cn(
        'flex items-center gap-2 text-xl font-normal tracking-tighter sm:text-2xl sm:tracking-tightest',
        icon || docsNode ? 'leading-none' : 'leading-tight',
        className,
      )}
    >
      {icon && (
        <span
          className="inline-flex shrink-0 items-center justify-center [&_svg]:block"
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
      {children}
      {docsNode && (
        <span
          className="inline-flex shrink-0 items-center self-center"
          data-testid="page-title-docs-slot"
        >
          {docsNode}
        </span>
      )}
    </h1>
  );
}

/**
 * Font stack shared by `PanelTitle` and `panelTitleTypography`.
 *
 * Keep the size step *after* this in every `cn()`. Tailwind's `text-*` also
 * sets a line height, so tailwind-merge drops a `leading-*` that precedes it —
 * which is why the `leading-tight` here does not survive into the rendered
 * class list in the common branch, and why anything hand-matching a title has
 * to repeat this exact order. Both consumers below compose it the same way, so
 * they agree; a call site writing the classes out by hand would not.
 *
 * TODO: `leading-tight` is therefore dead in the non-docs branches. Making it
 * apply is a real change to the vertical rhythm of every panel title in the
 * app, so it wants its own change with visual review, not a drive-by fix.
 */
const PANEL_TITLE_FONT = 'font-medium leading-tight tracking-tight';

/** Size step shared by `PanelTitle` and `panelTitleTypography`. */
function panelTitleSizeClass(size: 'sm' | 'default'): string {
  return size === 'sm' ? 'text-sm' : 'text-base';
}

/**
 * The typography a `PanelTitle` of this size renders, with no layout classes.
 *
 * For surfaces that must style a *different* element to match a `PanelTitle`.
 * The only such surface today is `InlineEditableText`: its edit-mode shell has
 * to carry the title's font metrics so the `<input>` inherits them. Both this
 * helper and `PanelTitle` are built from `PANEL_TITLE_FONT` and
 * `panelTitleSizeClass`, so the editing and read-only states cannot drift.
 *
 * Layout classes (`truncate`, `min-w-0`) are deliberately excluded — they would
 * tailwind-merge over the editable shell's own `inline-flex` / `max-w-` layout.
 *
 * Prefer `EditableAgentName` (web) over calling this directly: it derives the
 * title props and the shell classes from one `size`, so no call site hand-matches
 * anything.
 */
export function panelTitleTypography(size: 'sm' | 'default' = 'default'): string {
  return cn(PANEL_TITLE_FONT, panelTitleSizeClass(size));
}

/**
 * Compact heading for panels where the title sits beside an icon box or in a panel header bar.
 * Truncates by default — always place inside a `min-w-0` flex parent.
 * Pass `truncate={false}` to wrap. Intentionally smaller than PageTitle to balance
 * with adjacent iconography.
 */
export function PanelTitle({
  children,
  className,
  as: Tag = 'h1',
  size = 'default',
  truncate = true,
  docs,
}: PanelTitleProps) {
  const sizeClass = panelTitleSizeClass(size);
  const textClass = cn(
    PANEL_TITLE_FONT,
    truncate ? 'truncate' : 'min-w-0 whitespace-normal break-words',
  );

  const docsNode =
    typeof docs === 'string' ? (docsHintRenderer ? docsHintRenderer({ slug: docs }) : null) : docs;

  if (!docsNode) {
    return (
      <Tag data-testid="panel-title" className={cn(textClass, sizeClass, className)}>
        {children}
      </Tag>
    );
  }

  // Wrapped titles keep the docs affordance in normal inline flow so it sits
  // after the last word (not flex-pushed into the gap beside status chips).
  if (!truncate) {
    return (
      <Tag data-testid="panel-title" className={cn(textClass, sizeClass, className)}>
        {children}
        <span
          className="ml-1.5 inline-flex h-[1.25em] items-center align-middle"
          data-testid="panel-title-docs-slot"
        >
          {docsNode}
        </span>
      </Tag>
    );
  }

  return (
    <Tag
      data-testid="panel-title"
      className={cn('flex min-w-0 max-w-full items-center gap-1.5', sizeClass, className)}
    >
      <span className={textClass}>{children}</span>
      <span
        className="inline-flex shrink-0 items-center self-center"
        data-testid="panel-title-docs-slot"
      >
        {docsNode}
      </span>
    </Tag>
  );
}

// Tailwind only ships `line-clamp-1`..`line-clamp-6` as static utilities, so a
// dynamic `line-clamp-${n}` template string is invisible to its scanner. Index
// into this fixed map instead — every class is a literal the scanner sees.
const OVERFLOW_LINE_CLAMP: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

interface OverflowTextProps {
  children: React.ReactNode;
  /**
   * HTML element to render. Defaults to `span`, which fits inside buttons,
   * chips, and other inline-flex rows. Use `p` or `div` for block contexts.
   */
  as?: 'span' | 'p' | 'div';
  /**
   * How text that doesn't fit the measured box is handled. The element always
   * owns `min-w-0`, so it shrinks inside a flex parent and never pushes its
   * siblings out of the row — the recurring "label ran past the button or card
   * and got clipped by an `overflow-hidden` ancestor" bug (REF-135).
   *
   * - `'truncate'` (default): single-line ellipsis.
   * - `'clamp'`: ellipsis after `lines` lines.
   * - `'wrap'`: wrap to as many lines as needed, no ellipsis.
   */
  mode?: 'truncate' | 'clamp' | 'wrap';
  /**
   * Lines to keep when `mode="clamp"`. Defaults to 2 and is clamped to 1..6,
   * the range of Tailwind's built-in `line-clamp-*` utilities.
   */
  lines?: number;
  className?: string;
  title?: string;
  'data-testid'?: string;
}

/**
 * Inline text that owns its overflow handling. Drop it inside any flex row
 * (a `Button`, a chip, a card header) and it shrinks to fit the available
 * width — wrapping, clamping, or truncating per `mode` — instead of pushing
 * its siblings past the row and getting clipped by an `overflow-hidden`
 * ancestor. Unlike `PanelTitle`, this renders a `<span>` by default and
 * carries its own `min-w-0`, so callers can't misuse it by forgetting the
 * shrinking parent.
 */
export function OverflowText({
  children,
  as: Tag = 'span',
  mode = 'truncate',
  lines = 2,
  className,
  title,
  'data-testid': dataTestId,
}: OverflowTextProps) {
  const clamped = Math.max(1, Math.min(6, Math.trunc(lines)));
  const textClass =
    mode === 'wrap'
      ? 'min-w-0 break-words whitespace-normal'
      : mode === 'clamp'
        ? cn('min-w-0 break-words', OVERFLOW_LINE_CLAMP[clamped])
        : 'min-w-0 truncate';
  return (
    <Tag data-testid={dataTestId} title={title} className={cn(textClass, className)}>
      {children}
    </Tag>
  );
}

/**
 * Descriptive subtitle rendered below any heading.
 * Provides consistent color, weight, and top margin.
 */
export function PageDescription({
  children,
  className,
  'data-testid': dataTestId,
}: TypographyProps) {
  return (
    <p
      data-testid={dataTestId}
      className={cn(
        'mt-1 text-xs leading-normal text-muted-foreground/80 sm:text-sm sm:leading-relaxed',
        className,
      )}
    >
      {children}
    </p>
  );
}

/**
 * Sub-section divider label within a page body (e.g. "Active Agents", "Recent Runs").
 * Rendered as h2 by default with uppercase tracking for visual hierarchy below PageTitle.
 * Use the `as` prop to choose the correct semantic element for the context.
 */
export function SectionLabel({
  children,
  className,
  as: Tag = 'h2',
  tone = 'muted',
  'data-testid': dataTestId,
}: SectionLabelProps) {
  return (
    <Tag
      data-testid={dataTestId}
      className={cn(
        'text-2xs font-medium uppercase tracking-widest',
        tone === 'strong' ? 'text-foreground/70' : 'text-muted-foreground/60',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export interface InlineDocsHintProps {
  /**
   * Slug of the doc page to surface (e.g. `administration/organization-onboarding`).
   * May include a trailing `#anchor` to deep-link to a specific heading or block.
   */
  slug: string;
  /** Optional class names appended to the icon wrapper. */
  className?: string;
}

/**
 * Standalone docs-hint icon for inline use next to field labels or other
 * non-heading contexts. Goes through the same `docsHintRenderer` registry as
 * `PageTitle` / `PanelTitle`, so `@reflex/ui` stays decoupled from
 * `plugin-docs`. Renders nothing if no renderer has been registered (e.g. when
 * the docs plugin is disabled) or in tests/stories that don't stub it.
 */
export function InlineDocsHint({ slug, className }: InlineDocsHintProps) {
  if (!docsHintRenderer) return null;
  return (
    <span className={cn('inline-flex items-center', className)} data-testid="inline-docs-hint">
      {docsHintRenderer({ slug })}
    </span>
  );
}
